
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { calculateDiscountedPrice } from '@/lib/utils';

export interface CartItem {
  id: string;
  name: string;
  price: string;
  currency: string;
  image: string;
  quantity: number;
  discount?: number;
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  totalItems: () => number;
  totalPrice: () => number;
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      
      totalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },
      
      totalPrice: () => {
        return get().items.reduce((total, item) => {
          const priceNum = parseFloat(item.price);
          
          // Use the utility function to calculate discounted price
          const itemPrice = item.discount 
            ? calculateDiscountedPrice(priceNum, item.discount)
            : priceNum;
            
          return total + (itemPrice * item.quantity);
        }, 0);
      },
      
      addItem: (newItem) => {
        const { items } = get();
        const existingItem = items.find(item => item.id === newItem.id);
        
        if (existingItem) {
          set({
            items: items.map(item => 
              item.id === newItem.id 
                ? { ...item, quantity: item.quantity + (newItem.quantity || 1) }
                : item
            )
          });
        } else {
          set({ items: [...items, { ...newItem, quantity: newItem.quantity || 1 }] });
        }
        
        // Open cart when adding items
        get().openCart();
      },
      
      removeItem: (id) => {
        const { items } = get();
        
        set({ 
          items: items.filter(item => item.id !== id)
        });
      },
      
      updateQuantity: (id, quantity) => {
        const { items } = get();
        
        if (quantity <= 0) {
          get().removeItem(id);
          return;
        }
        
        set({
          items: items.map(item => 
            item.id === id ? { ...item, quantity } : item
          )
        });
      },
      
      clearCart: () => {
        set({ items: [] });
      },
      
      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      toggleCart: () => set(state => ({ isOpen: !state.isOpen })),
    }),
    {
      name: 'ahavex-cart',
    }
  )
);
